/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { getToken } from '../../AuthService'
import * as default_data from '../../config.js'
import { validateFile } from '../../Utils/file_functions.js'
import GooglePopup from './GooglePopup.js'
import { FiTrash2, FiUploadCloud } from 'react-icons/fi';
import { Box, Dialog, Chip, Divider, DialogTitle, IconButton, Button,  DialogActions, CircularProgress, useTheme } from '@mui/material';
import { IoClose } from "react-icons/io5";
import { ScrollableDialogContent } from '../../Components/ScrollableDialogContent.js';
import { useGoogleOAuth } from '../../Contexts/GoogleContext.js';

const FileUploadItem = React.forwardRef(({ file, workbook, addSource, deleteFile }, ref) => {

  const theme = useTheme()

  React.useImperativeHandle(ref, () => ({
    submitFile,
  }));

  const customStyles = {
    dropzoneitem: {
      listStyleType: 'none',
      textAlign: 'left',
      padding: '10px',
      margin: '10px',
      border: '1px solid #ccc',
      borderRadius: '5px',
    }
  };
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [icon, setIcon] = useState(null);

  async function submitFile(dataset) {
    setIcon(null);
    setErrorMessage('');
    setSuccessMessage('');
  
    var token = getToken();
    if (token === false) {
      return;
    }
  
    if (validateFile(file.file) === false) {
      setErrorMessage('Invalid file type or size. Please refer to our documentation for the list of supported file types and sizes.');
      setIcon('failure');
      return;
    }
  
    setIcon('spinner');
  
    const formData = new FormData();
    formData.append('file', file.file);
    formData.append('dataset', dataset);
  
    try {
      const response = await fetch(default_data.default_chat_service_url_files + '/train-with-file', {
        method: 'POST',
        headers: {
          'x-access-tokens': `${token}`
        },
        body: formData,
        timeout: 3600000 // 60 minutes in milliseconds
      });
  
      if (!response.ok) {
        throw new Error('An error occurred while getting data.');
      }
  
      const data = await response.json();
  
      if (data['status'] === 200) {
        if (data['response'].includes('Sorry,')) {
          setErrorMessage(data['response']);
          setIcon('failure');
        } else {
          var source = await addSource(workbook.id, 'filename', file.file);
          if (source['status'] === 200) {
            setSuccessMessage(data['response']);
            setIcon('success');
          } else {
            setErrorMessage(source['response']);
            setIcon('failure');
          }
        }
      } else {
        if (data['response'].includes('Sorry,')) {
          setErrorMessage(data['response']);
          setIcon('failure');
        } else {
          setErrorMessage('Internal error, please contact the customer support [1].');
          setIcon('failure');
        }
      }
    } catch (error) {
      console.log(error);
      setErrorMessage('Internal error, please contact the customer support [2].');
      setIcon('failure');
    }
  }
  
  return (      
    <li style={customStyles.dropzoneitem}>
      <div style={{ display:'flex', alignItems: 'center'}}>
        {file.file.name}
        <button className="chat-log-copy-btn" onClick={(e) => {
          e.stopPropagation();
          deleteFile(file.id);
        }}>
          <FiTrash2 color={theme.palette.text.primary} />
        </button>       
        { icon === 'spinner' && (
          <CircularProgress size={30} alt="loading" />
        )} 
        { (icon === 'success' || icon === 'failure') && 
          <img style={{width:'30px'}} src={icon + '.png'} alt="{icon}" /> 
        }
      </div>
      {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
      {successMessage && <p style={{ color: 'green', alignSelf: 'center' }}>{successMessage}</p>}
    </li>
  );
});

const FilePopup = ({ workbook, loadSources, addSource, dataset, paid, triggerFilePopup, ingestURL, setTriggerFilePopup, loadQuestions, generateOverallSummary }) => {
  const [loaded, setLoaded] = useState(false);
  var loaded_static = false;
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileRefs, setFileRefs] = React.useState([]);
  const googlePopupRef = useRef();
  const isGoogleOAuthEnabled = useGoogleOAuth();
  const theme = useTheme()

  function closeModal() {
    setIsLoading(false);
    setTriggerFilePopup(0);
    setUploadedFiles([]);
    setIsOpen(false);
  }

  useEffect(() => {
    setFileRefs((refs) =>
      Array(uploadedFiles.length)
        .fill()
        .map((_, i) => refs[i] || React.createRef())
    );
  }, [uploadedFiles]);

  useEffect(() => {
    if (triggerFilePopup) {
      if (triggerFilePopup === 1) {
        setIsOpen(true)
      }
    }
  }, [triggerFilePopup]);

  useEffect(() => {
    if(loaded || loaded_static) {
      return;
    }

    setLoaded(true)
    loaded_static = true;
}, [])

    const customStyles = {
    overlay: {
      position: 'fixed',
      zIndex: 1019,
      top: 0,
      left: 0,
      width: '100vw',
      borderRadius: '0.375rem',
      height: '100vh',
      overflow: 'hidden',
      background: 'rgba(255, 255, 255, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      background: '#050038',
      color: '#e9ecef',      
      width: '90%',
      height: '90%',
      margin: '5%',
      inset: 0,
      borderRadius: '30px 10px 10px 30px',
      maxWidth: 'calc(100vw - 2rem)',
      maxHeight: 'calc(100vh - 2rem)',
      overflow: 'auto',
      position: 'relative',
      border: '1px solid #ccc'
    },
    dropzone: {
      border: '2px dashed #ccc',
      borderRadius: '5px',
      padding: '20px',
      textAlign: 'center',
      cursor: 'pointer',
    },
    title: {
      textAlign: 'left',
      marginBottom: '20px',
      fontSize: '1.2em'
    },
    urlSection: {
      backgroundColor: theme.palette.appBackground,
      padding: '20px',
      borderRadius: '4px',
      textAlign: 'center',
      margin: '10px'
    },
    fileSection: {
      backgroundColor: theme.palette.appBackground,
      padding: '20px',
      borderRadius: '4px',
      textAlign: 'center',
      margin: '10px'
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      const updatedFiles = acceptedFiles.filter((file) => {
        // Check if the file name already exists in the uploadedFiles array
        return !uploadedFiles.some((uploadedFile) => uploadedFile.file.name === file.name);
      }).map((file) => ({
        id: file.name,
        file: file,
      }));
      setUploadedFiles([...uploadedFiles, ...updatedFiles]);
    },
  });

  const deleteFile = (id) => {
    const updatedFiles = uploadedFiles.filter((file) => file.id !== id);
    setUploadedFiles(updatedFiles);
  };

  const [errorMessage, setErrorMessage] = useState('');

  async function submitFiles() {
    setErrorMessage('');
    if (dataset === undefined || dataset === null || dataset === '') {
      setErrorMessage('You cannot use "Default" as a dataset, please select a dataset to ingest into or create a new one.');
      return;
    }
    setIsLoading(true);
  
    // Collect all file submission promises
    const fileSubmissionPromises = fileRefs.map(ref => {
      if (ref.current) {
        return ref.current.submitFile(dataset);
      }
      return Promise.resolve(); // If ref.current is not defined, return a resolved promise
    });
  
    // If Google OAuth is enabled and the user is paid, add the Google file submission promise
    if (paid === true && default_data.default_has_google_login_enabled === true && isGoogleOAuthEnabled) {
      if (googlePopupRef && googlePopupRef.current) {
        fileSubmissionPromises.push(googlePopupRef.current.submitFiles(dataset));
      }
    }
  
    // Wait for all file submissions to complete
    await Promise.all(fileSubmissionPromises);
  
    // Load sources and set loading state to false
    var cur_sources = await loadSources();
    generateOverallSummary(cur_sources);
    loadQuestions(cur_sources);
    setIsLoading(false);
  }
  
  return (
    <Dialog open={modalIsOpen} onClose={closeModal} fullWidth maxWidth="xl">
    <DialogTitle>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Ingest Sources
        <IconButton onClick={closeModal}>
          <IoClose />
        </IconButton>
      </Box>
    </DialogTitle>
    <ScrollableDialogContent modalIsOpen={modalIsOpen}>
      <div style={customStyles.fileSection}>
        <h4 style={customStyles.title}>Ingest File</h4>
        <div {...getRootProps()} style={customStyles.dropzone}>
          <input {...getInputProps()} />
          <FiUploadCloud size={48} color="#1976d2" />
          <div dangerouslySetInnerHTML={{ __html: default_data.default_file_allowed_txt}}></div>
          <ul>
            {uploadedFiles.map((file, i) => (
              <FileUploadItem
                workbook={workbook}
                addSource={addSource}
                key={file.id}
                ref={fileRefs[i]}
                deleteFile={deleteFile}
                file={file}
              />
            ))}
          </ul>
        </div>
        {paid && default_data.default_has_google_login_enabled && isGoogleOAuthEnabled && (
          <>
            <Divider sx={{ my: 3 }}>
              <Chip label="OR" />
            </Divider>
            <div style={{ margin: 'auto' }}>
              <GooglePopup workbook={workbook} addSource={addSource} ref={googlePopupRef} showPicker="true" />
              <br />
            </div>
          </>
        )}
        {errorMessage && <p style={{ color: 'red', marginTop: 8 }}>{errorMessage}</p>}
        <DialogActions>
          <Button onClick={submitFiles} disabled={isLoading} variant="contained">Submit</Button>
        </DialogActions>
      </div>
      {paid && !default_data.default_block_live && (
        <div style={customStyles.urlSection}>
          <h4 style={customStyles.title}>Ingest Website URL</h4>
          <Button onClick={ingestURL} variant="contained">Ingest Web URL</Button>
        </div>
      )}
    </ScrollableDialogContent>
    <DialogActions>
      <Button onClick={closeModal} color="error">Close</Button>
    </DialogActions>
  </Dialog>
  );
}
export default FilePopup;